<template>
  <div class="prozess-wrapper">
    <div class="prozess">
      <div>
        <div v-if="!claimed">
          <TaskBlocker :assignee="data.assignee" />
        </div>
        <div v-if="!taskDataLoading">
          <v-alert
            text
            type="info"
            class="mt-4"
          >
            Für Carrier Code <code>{{ validatePortingProviderCode }}</code> gibt
            es mehrere Carrier in varioCRM. Um keine falschen Anfragen zu
            erstellen, muss der korrekte Carrier ausgewählt werden.
          </v-alert>

          <CustomerDetails
            v-if="customerData !== null"
            :data="customerData"
            title="Kundeninformationen"
          />

          <div class="group">
            <div>Portierungsdaten</div>
            <div v-if="portingData !== null">
              <div class="input-row">
                <div>
                  <div class="label">
                    Vertragsnummer
                  </div>
                  <div>
                    {{ portingData.contractId }}
                  </div>
                </div>
                <div>
                  <div class="label">
                    Gewünschtes Portierungsdatum
                  </div>
                  <div>
                    {{
                      new Date(
                        portingData.requestedPortingDate
                      ).toLocaleDateString('de-DE', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric'
                      })
                    }}
                  </div>
                </div>
              </div>
              <div class="input-row">
                <div>
                  <div class="label">
                    Zu portierende Rufnummern
                  </div>
                  <div>
                    <div
                      v-for="number in portingData.phoneNumbers"
                      :key="number"
                    >
                      {{ number }}
                    </div>
                  </div>
                </div>
                <div>
                  <div class="label">
                    Alle Rufnummern portieren?
                  </div>
                  <div>
                    {{ portingData.portAllPhoneNumbers ? 'Ja' : 'Nein' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <CarrierSelection
            v-model="selectedCarrier"
            :disabled="!claimed"
            :carrier-code-filter="validatePortingProviderCode"
            :errorMessage="
              selectedCarrier === null
                ? 'Es muss ein Carrier ausgewählt werden!'
                : null
            "
          />
        </div>

        <div v-else>
          <Loader />
        </div>
      </div>

      <div class="mb-4 buttons-wrapper">
        <v-btn
          :disabled="taskDataLoading || selectedCarrier === null || !claimed"
          :loading="isSubmitting"
          color="primary"
          large
          @click="completeTaskDialog = true"
        >
          Aufgabe abschließen
        </v-btn>
      </div>

      <v-dialog v-model="completeTaskDialog" width="max(20vw, 350px)">
        <v-card v-if="selectedCarrier !== null">
          <v-card-text class="pt-6">
            <p>
              Soll der Carrier <b>{{ selectedCarrier.name }}</b> übernommen
              werden?
            </p>
          </v-card-text>
          <v-card-actions class="pb-3">
            <v-btn
              :disabled="isSubmitting"
              small
              color="secondary"
              class="align-center"
              @click="completeTaskDialog = !completeTaskDialog"
            >
              Abbrechen
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              :loading="isSubmitting"
              small
              color="warning"
              class="align-center"
              @click="completeTask()"
            >
              Carrier übernehmen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import { HTTP } from '@/main/httpClient.js';
import NotificationObject from '@/main/NotificationObject.js';

import CustomerDetails from '@/components/CustomerDetails.vue';
import NormalButton from '@/components/elements/NormalButton.vue';
import Loader from '@/components/elements/Loader.vue';
import TaskBlocker from '../TaskBlocker.vue';
import ErrorMessageBuilder from '@/util/ErrorMessageBuilder.js';

import CarrierSelection from '@/components/CarrierSelectionVuetify.vue';

export default {
  name: 'ActionCarrierAuswahlen',
  components: {
    NormalButton,
    TaskBlocker,
    CustomerDetails,
    Loader,
    CarrierSelection
  },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    taskDataLoading: true,
    customerData: null,
    portingData: null,
    selectedCarrier: null,
    validatePortingProviderCode: null,
    completeTaskDialog: false,
    isSubmitting: false
  }),
  computed: {
    username() {
      return this.$store.getters.getUsername;
    },
    claimed() {
      return this.data.assignee === this.username;
    },
    carriers() {
      if (this.portingData !== null) {
        return [
          this.portingData.dimariCarrierDto,
          this.portingData.ocsCarrierDto
        ];
      } else return [];
    }
  },
  mounted: function () {
    this.loadTaskData();
  },
  methods: {
    showMessage: function (type, description) {
      this.$store.commit(
        'addNotification',
        new NotificationObject(type, description)
      );
    },
    loadTaskData: function () {
      return new Promise((resolve) => {
        this.taskDataLoading = true;
        this.data.variables
          .then((data) => {
            this.customerData = data.customer.value;
            this.portingData = data.porting.value;
            this.selectedCarrier = this.portingData.ocsCarrierDto;
            this.validatePortingProviderCode =
              data.validatePortingProviderCode.value;
          })
          .finally(() => {
            this.taskDataLoading = false;
            return resolve();
          });
      });
    },
    completeTask: function () {
      this.isSubmitting = true;
      HTTP.post(`/task/${this.data.id}/complete`, {
        variables: {
          currentProvider: {
            type: 'Object',
            value: JSON.stringify(this.selectedCarrier),
            valueInfo: {
              objectTypeName: 'de.goetel.spot.porting.entity.CarrierDto',
              serializationDataFormat: 'application/json'
            }
          }
        }
      })
        .then((resp) => {
          this.showMessage(
            'success',
            `Die Aufgabe wurde abgeschlossen und ${this.selectedCarrier.name} als Carrier festgelegt.`
          );
          this.$store.commit('triggerTasksRefresh');
          this.$emit('onClose');
        })
        .catch((err) =>
          this.showMessage(
            'error',
            ErrorMessageBuilder.buildErrorMessage(
              err.response.data,
              'Der Abschluss der Aufgabe war nicht erfolgreich!'
            )
          )
        )
        .finally(() => {
          this.isSubmitting = false;
        });
    }
  }
};
</script>

<style scoped>
.buttons-wrapper {
  display: grid;
  grid-template-columns: auto max-content;
  grid-gap: var(--goe-spacing-1);
}

.buttons-wrapper .v-btn {
  width: max-content;
  margin-left: auto;
  margin-right: var(--goe-spacing-0);
}

.loader {
  margin: auto;
}

.description {
  font-size: var(--goe-fontSize-small);
  margin-top: var(--goe-spacing-0_5);
}

.group > div:nth-child(2) > div {
  margin-bottom: var(--goe-spacing-2);
}

.group > div:nth-child(2) > div:last-child {
  margin-bottom: var(--goe-spacing-0);
}

.label {
  width: calc(100% - 4px);
}

.buttons-wrapper .v-btn {
  width: max-content;
  margin-left: auto;
  margin-right: var(--goe-spacing-0);
}

.v-autocomplete {
  width: calc(100% - 4px);
}
</style>
